import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";

import { Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";
import useAppStore from "@duck/app-store";
import usePermission from "@util/usePermission";
import { fetchOrgUsers, orgJoinSelectLabel } from "./organizationMemberShared";
import AssessmentViewerPicker from "./AssessmentViewerPicker";
import AssessmentViewerLinksPanel from "./AssessmentViewerLinksPanel";
import { orgInstrumentSelectOptions } from "./orgInstrumentSelectOptions";

export default function OrganizationAssessmentAssign({ organizationId }) {
  const { t } = useTranslation();
  const admin = useAppStore((state) => state.admin);
  const isPlatformAdmin = !!admin;

  const canPlatformAssign = usePermission(
    PERMISSIONS.ASSESSMENT_INSTRUMENTS_EDIT
  );
  const canOrgAssign = usePermission(PERMISSIONS.ASSESSMENTS_ASSIGN);
  const canUseForm = isPlatformAdmin ? canPlatformAssign : canOrgAssign;

  const [userId, setUserId] = useState(null);
  const [instrumentId, setInstrumentId] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [instrumentOptions, setInstrumentOptions] = useState([]);
  const [viewerKeys, setViewerKeys] = useState([]);
  const [viewerIds, setViewerIds] = useState({
    organization_user_admin_ids: [],
    organization_user_manager_ids: [],
  });
  const [sendEmail, setSendEmail] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [assignedAssessmentId, setAssignedAssessmentId] = useState(null);
  const [assignedUserId, setAssignedUserId] = useState(null);
  const [assignedUserLabel, setAssignedUserLabel] = useState(null);
  const [assignedInstrumentLabel, setAssignedInstrumentLabel] = useState(null);

  useEffect(() => {
    if (!canUseForm) return;
    setLoadingOptions(true);
    Promise.all([
      fetchOrgUsers(organizationId),
      axios.post(
        `/organizations/${organizationId}/retrieve-assessment-instruments/`,
        { page: 0, page_size: 200 }
      ),
      axios.post(`/retrieve-assessment-instruments/`, {
        scope: "active",
        page: 0,
        page_size: 200,
      }),
    ])
      .then(([users, instrumentsRes, catalogRes]) => {
        setUserOptions(
          (users || []).map((row) => ({
            value: String(row.user_id),
            label: orgJoinSelectLabel(row, row.user_id),
          }))
        );
        const joined = instrumentsRes.data.response[0]?.data || [];
        const catalog = catalogRes.data.response[0]?.data || [];
        setInstrumentOptions(orgInstrumentSelectOptions(joined, catalog, t));
        setLoadingOptions(false);
      })
      .catch((err) => {
        setLoadingOptions(false);
        toast.error(err);
      });
  }, [organizationId, canUseForm]);

  function resetViewers() {
    setViewerKeys([]);
    setViewerIds({
      organization_user_admin_ids: [],
      organization_user_manager_ids: [],
    });
  }

  function submit() {
    if (!userId || !instrumentId) return;
    setSubmitting(true);

    const body = { user_id: userId };
    if (viewerIds.organization_user_admin_ids.length) {
      body.organization_user_admin_ids = viewerIds.organization_user_admin_ids;
    }
    if (viewerIds.organization_user_manager_ids.length) {
      body.organization_user_manager_ids =
        viewerIds.organization_user_manager_ids;
    }

    let url;
    if (isPlatformAdmin) {
      url = `/assessment-instruments/${instrumentId}/assign/`;
      body.organization_id = organizationId;
      body.send_email = sendEmail;
    } else {
      url = `/organizations/${organizationId}/assessment-instruments/${instrumentId}/assign/`;
    }

    const userLabel =
      userOptions.find((option) => option.value === String(userId))?.label ||
      String(userId);
    const instrumentLabel =
      instrumentOptions.find((option) => option.value === String(instrumentId))
        ?.label || String(instrumentId);

    axios
      .post(url, body)
      .then(({ data }) => {
        const payload = data.response[0] || {};
        const assessment = payload.assessment || {};
        setSubmitting(false);
        setAssignedAssessmentId(assessment.id || null);
        setAssignedUserId(userId);
        setAssignedUserLabel(userLabel);
        setAssignedInstrumentLabel(instrumentLabel);
        setUserId(null);
        setInstrumentId(null);
        resetViewers();
        toast.success(t("updated"));
      })
      .catch((err) => {
        setSubmitting(false);
        toast.error(err);
      });
  }

  if (!canUseForm) {
    return (
      <EmptyMessage
        title={t("no_permission")}
        text={t("no_permission_description")}
      />
    );
  }

  const gatePermission = isPlatformAdmin
    ? PERMISSIONS.ASSESSMENT_INSTRUMENTS_EDIT
    : PERMISSIONS.ASSESSMENTS_ASSIGN;

  return (
    <Can permission={gatePermission}>
      <Stack gap="md">
        <Title order={5}>{t("assign_assessment")}</Title>
        <Card>
          <Stack gap="sm">
            <Select
              label={t("user_singular")}
              searchable
              clearable
              data={userOptions}
              value={userId}
              onChange={(value) => {
                setUserId(value);
                resetViewers();
              }}
              disabled={loadingOptions}
            />
            <Select
              label={t("assessment_instrument_singular")}
              searchable
              clearable
              data={instrumentOptions}
              value={instrumentId}
              onChange={setInstrumentId}
              disabled={loadingOptions}
            />
            <AssessmentViewerPicker
              organizationId={organizationId}
              userId={userId}
              mode="multi"
              value={viewerKeys}
              onChange={({
                keys,
                organization_user_admin_ids,
                organization_user_manager_ids,
              }) => {
                setViewerKeys(keys || []);
                setViewerIds({
                  organization_user_admin_ids:
                    organization_user_admin_ids || [],
                  organization_user_manager_ids:
                    organization_user_manager_ids || [],
                });
              }}
            />
            {isPlatformAdmin && (
              <Checkbox
                label={t("send_email")}
                checked={sendEmail}
                onChange={(event) => setSendEmail(event.currentTarget.checked)}
              />
            )}
            <Button
              disabled={!userId || !instrumentId}
              loading={submitting}
              onClick={submit}
            >
              {t("assign")}
            </Button>
          </Stack>
        </Card>

        {assignedAssessmentId && (
          <Card>
            <Stack gap="xs" mb="sm">
              <Text fw={600}>{t("assessment_assigned")}</Text>
              <Text size="sm">
                {t("user_singular")}: {assignedUserLabel || assignedUserId}
              </Text>
              <Text size="sm">
                {t("assessment_instrument_singular")}:{" "}
                {assignedInstrumentLabel || "—"}
              </Text>
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  {t("assessment_singular")}: {assignedAssessmentId}
                </Text>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => {
                    navigator.clipboard
                      ?.writeText(String(assignedAssessmentId))
                      .then(() => toast.success(t("copied")))
                      .catch(() => toast.error(t("copy_failed")));
                  }}
                >
                  {t("copy")}
                </Button>
              </Group>
            </Stack>
            <Text size="sm" mb="xs">
              {t("assessment_viewer_plural")}
            </Text>
            <AssessmentViewerLinksPanel
              organizationId={organizationId}
              assessmentId={assignedAssessmentId}
              assigneeUserId={assignedUserId}
              allowMutate
            />
          </Card>
        )}
      </Stack>
    </Can>
  );
}
